import React, {useCallback, useMemo, useState} from "react";
import {DialogActions, DialogContent, DialogTitle} from "@mui/material";
import Dialog from "../../base/dialog";
import Button from "../../base/button/button";
import {DataGridColumnAlignments, DataGridColumnTypes, DataGridColumnWidthTypes, useDataGridSavedState} from "../../../../packages/data-grid";
import useIsMounted from "../../../hooks/use-is-mounted";
import PartnerDataGrid from "../../../containers/data-grid";
import {BackOrderGenerationDataGridColumnNames} from "../../../../core/constants/table/data-grid-column-keys";
import ProductInformationPopover from "../../app-specific/draggable-popover/product-information-popover";
import useAccessLevels from "../../../hooks/use-access-levels";

const dataGridSaveConfig = {
    name: 'BackOrderGenerationConfirmationDialogDataGrid',
    version: 1,
}

const BackOrderGenerationConfirmationDialog = ({
                                                   open,
                                                   close,
                                                   items,
                                                   onAccept,
                                               }) => {
    const operatorAccessLevels = useAccessLevels();
    const [accepting, setAccepting] = useState(false);
    const isMounted = useIsMounted();

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: "Part No",
                name: BackOrderGenerationDataGridColumnNames.partNo,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: {
                    size: 1,
                    type: DataGridColumnWidthTypes.flex,
                },
            },
            {
                title: "Back-order Quantity",
                name: BackOrderGenerationDataGridColumnNames.backorderQty,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: {
                    size: 1,
                    type: DataGridColumnWidthTypes.flex,
                },
            },
        ], [])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const products = useMemo(() => {
        const res = [];
        items?.forEach(e => {
            if (parseFloat(e.backorderQty)) {
                const value = operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders ? parseFloat(e.backorderQty) : parseInt(e.backorderQty);
                res.push({
                    partNo: e.partNo,
                    backorderQty: value,
                });
            }
        })
        return res;
    }, [items, operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders])

    /**
     * Submits the order with the given flag.
     *
     * - if the result flag of the callback is successful, closes the dialog.
     * @param withBackorders
     * @return {Promise<void>}
     */
    const submitOrder = useCallback(async (withBackorders) => {
        setAccepting(true);
        const resultFlag = await onAccept(withBackorders);
        if (!isMounted())
            return;
        setAccepting(false);
        if (resultFlag) {
            close();
        }
    }, [onAccept, close, isMounted]);

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return products.map(product => ({
            key: product.partNo,
            data: product,
            cells: {
                [BackOrderGenerationDataGridColumnNames.partNo]:
                    <ProductInformationPopover partNo={product.partNo}>
                        {product.partNo ?? ""}
                    </ProductInformationPopover>
                ,
                [BackOrderGenerationDataGridColumnNames.backorderQty]: product.backorderQty,
            },
        }))
    }, [products])

    return (
        <Dialog
            open={open}
            setOpen={close}
            maxWidth={'sm'}
            className={'bound-dialog'}
        >
            <DialogTitle className={'p-0'} id="scroll-dialog-title">
                <p className={'text-lg text-color-1 p-4 font-weight-semi-bold text-center'}>
                    Back-order Confirmation
                </p>
            </DialogTitle>
            <DialogContent dividers className={'p-4'}>
                <div className={'d-flex flex-column w-100'}>
                    <p className={'text-sm text-color-3'}>
                        Do you want to Add the following items as the <span
                        className={'black font-weight-semi-bold'}>back-order</span> ?
                    </p>
                </div>
                <PartnerDataGrid
                    {...dataGridSaveConfig}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    hideFooter
                    hideToolbar
                    classNames={{
                        container: "px-0"
                    }}
                    rows={dataGridRows}
                    columns={dataGridColumns}
                    state={{
                        density: dataGridDensity,
                    }}
                />
            </DialogContent>
            <DialogActions className={'p-0 w-100 d-flex justify-content-between'}>
                <Button
                    disabled={!open || accepting}
                    variant={'dialog'}
                    color={'primary'}
                    onClick={() => submitOrder(false)}
                    borderRadius={'0 8px 0 8px'}
                >
                    {accepting ? 'submitting...' : 'Submit Without Back-order'}
                </Button>
                <Button
                    disabled={!open || accepting}
                    variant={'dialog'}
                    color={'primary'}
                    onClick={() => submitOrder(true)}
                    borderRadius={'8px 0 8px 0'}
                >
                    {accepting ? 'submitting...' : 'Submit Back-order'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}


export default BackOrderGenerationConfirmationDialog;
